import {
	browserSupportsWebAuthn,
	startAuthentication,
	startRegistration,
} from "@simplewebauthn/browser";
import type {
	PublicKeyCredentialCreationOptionsJSON,
	PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

import { TPasskey, TToken } from "@/types";

import API, { publicClient } from "./api";

type TOptions<T> = { challengeId: string; publicKey: T };

/**
 * The person closed the device's prompt, or it timed out: nothing went
 * wrong, nothing to say. The browser reports both as NotAllowedError.
 */
export const isCancelled = (error: unknown): boolean =>
	error instanceof Error &&
	(error.name === "NotAllowedError" || error.name === "AbortError");

/**
 * Passkeys (WebAuthn). The API hands out the options and a single-use
 * challengeId; the browser asks the device (fingerprint, face, PIN) and
 * signs; the API verifies. Everything travels base64url, which is what
 * SimpleWebAuthn reads and writes.
 */
class PasskeysClass {
	/** False on a browser without WebAuthn: the buttons are not offered. */
	isSupported = (): boolean => browserSupportsWebAuthn();

	list = (): Promise<TPasskey[]> =>
		API.get("me/passkeys").then((res) => res.data);

	/** Asks the device for a new passkey and saves it under `name`. */
	add = async (name: string): Promise<TPasskey> => {
		const options: TOptions<PublicKeyCredentialCreationOptionsJSON> =
			await API.post("me/passkeys/options").then((res) => res.data);
		const credential = await startRegistration({
			optionsJSON: options.publicKey,
		});

		return API.post("me/passkeys", {
			challengeId: options.challengeId,
			name,
			clientDataJSON: credential.response.clientDataJSON,
			attestationObject: credential.response.attestationObject,
		}).then((res) => res.data);
	};

	rename = (id: number, name: string): Promise<TPasskey> =>
		API.patch(`me/passkeys/${id}`, { name }).then((res) => res.data);

	/** 409 AUTH.LAST_SIGN_IN_METHOD when it is the only way left in. */
	remove = (id: number): Promise<void> =>
		API.delete(`me/passkeys/${id}`).then(() => undefined);

	/** Signs in with whichever passkey the device offers: no address asked. */
	signIn = async (): Promise<TToken> => {
		const options: TOptions<PublicKeyCredentialRequestOptionsJSON> =
			await publicClient
				.post("auth/passkey/options")
				.then((res) => res.data);
		const assertion = await startAuthentication({
			optionsJSON: options.publicKey,
		});

		return publicClient
			.post(
				"auth/passkey",
				{
					challengeId: options.challengeId,
					id: assertion.id,
					clientDataJSON: assertion.response.clientDataJSON,
					authenticatorData: assertion.response.authenticatorData,
					signature: assertion.response.signature,
					userHandle: assertion.response.userHandle ?? null,
				},
				{
					headers: {
						"X-Timezone":
							Intl.DateTimeFormat().resolvedOptions().timeZone,
					},
					withCredentials: true,
				}
			)
			.then((res) => res.data);
	};
}

const PasskeysService = new PasskeysClass();

export default PasskeysService;
